<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const ownChannels = ref(true);
const fastReplies = ref(true);
const fastStreak = ref(false);
const aiDetection = ref(true);
const aiAllowed = ref(false);
const hydrating = ref(false);
const snapshot = ref('');
const serialized = () =>
  JSON.stringify([
    ownChannels.value,
    fastReplies.value,
    fastStreak.value,
    aiDetection.value,
  ]);

const enabled = value => value !== false && value !== '0' && value !== 0;

watch(
  currentAccount,
  account => {
    hydrating.value = true;
    const settings = account?.settings || {};
    ownChannels.value = enabled(settings.arrows_ai_block_own_channels);
    fastReplies.value = enabled(settings.arrows_ai_block_fast_replies);
    fastStreak.value = settings.arrows_ai_block_fast_streak === true;
    aiDetection.value = enabled(settings.arrows_ai_bot_detection);
    aiAllowed.value = settings.arrows_ai_bot_detection_allowed === true;
    snapshot.value = serialized();
    hydrating.value = false;
  },
  { immediate: true }
);

const save = async () => {
  if (hydrating.value || serialized() === snapshot.value) return;

  const previous = snapshot.value;
  snapshot.value = serialized();
  try {
    await updateAccount({
      arrows_ai_block_own_channels: ownChannels.value,
      arrows_ai_block_fast_replies: fastReplies.value,
      arrows_ai_block_fast_streak: fastStreak.value,
      ...(aiAllowed.value && { arrows_ai_bot_detection: aiDetection.value }),
    });
    useAlert(t('ARROWS_AI.SETTINGS.BOT_GUARD.SAVED'));
  } catch (error) {
    hydrating.value = true;
    snapshot.value = previous;
    const [own, fast, streak, ai] = JSON.parse(
      previous || '[true,true,false,true]'
    );
    ownChannels.value = own;
    fastReplies.value = fast;
    fastStreak.value = streak;
    aiDetection.value = ai;
    hydrating.value = false;
    useAlert(t('ARROWS_AI.SETTINGS.BOT_GUARD.ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('ARROWS_AI.SETTINGS.BOT_GUARD.TITLE') }}
      </h3>
      <p class="mt-1 mb-0 text-sm text-n-slate-11">
        {{ t('ARROWS_AI.SETTINGS.BOT_GUARD.DESCRIPTION') }}
      </p>
    </div>
    <SettingsToggleSection
      v-model="ownChannels"
      :header="t('ARROWS_AI.SETTINGS.BOT_GUARD.OWN_CHANNEL')"
      :description="t('ARROWS_AI.SETTINGS.BOT_GUARD.OWN_CHANNEL_DESC')"
      @update:model-value="save"
    />
    <SettingsToggleSection
      v-model="fastReplies"
      :header="t('ARROWS_AI.SETTINGS.BOT_GUARD.FAST_REPLIES')"
      :description="t('ARROWS_AI.SETTINGS.BOT_GUARD.FAST_REPLIES_DESC')"
      @update:model-value="save"
    />
    <SettingsToggleSection
      v-model="fastStreak"
      :header="t('ARROWS_AI.SETTINGS.BOT_GUARD.FAST_STREAK')"
      :description="t('ARROWS_AI.SETTINGS.BOT_GUARD.FAST_STREAK_DESC')"
      @update:model-value="save"
    />
    <SettingsToggleSection
      v-if="aiAllowed"
      v-model="aiDetection"
      :header="t('ARROWS_AI.SETTINGS.BOT_GUARD.AI_DETECTION')"
      :description="t('ARROWS_AI.SETTINGS.BOT_GUARD.AI_DETECTION_DESC')"
      @update:model-value="save"
    />
  </div>
</template>
